import { useContext, useEffect, useState } from 'react';
import Highcharts from 'highcharts';

import DashboardContextMenuContext from 'contexts/DashboardContextMenuContext';
import getGroupByField from 'hooks/useMetricMatrix/getGroupByField';
import useMetricMatrix from 'hooks/useMetricMatrix';
import useChartDrillDowns from 'hooks/useChartDrillDowns';
import useValueFormatters from 'hooks/useValueFormatters';
import to2DMatrixCells from '../to2DMatrixCells';
import sankey from '../highchartOptions/sankeyOptions';
import toSankeyLinks from '../toSankeyLinks';

// Sankey reuses the two-dimension metric-matrix pipeline (dimensionA sources x
// dimensionB targets, single metric weight) and turns each non-zero cell into a
// weighted link.
const useSankeyData = ({
  chartDefinition,
  chartRef,
}: {
  chartDefinition: V5ChartDefinition;
  chartRef: React.MutableRefObject<any>;
}): {
  options: Highcharts.Options | undefined;
  isNoData: boolean;
  isReady: boolean;
} => {
  const { openMenu } = useContext(DashboardContextMenuContext);

  // Derive (don't freeze in useState) — the gadget can mount before dimensionA
  // is set in the config modal, which would otherwise lock groupField to ''.
  const groupField = getGroupByField(chartDefinition);
  const [options, setOptions] = useState<Highcharts.Options | undefined>();
  const [isNoDataToDisplay, setIsNoDataToDisplay] = useState<boolean>(false);

  const { formatMetric } = useValueFormatters();
  const { columnDefs, rowData, isInitialLoading } = useMetricMatrix({
    chartDef: chartDefinition,
    skip2DRowDataFormatting: true,
  });
  const { onDrillDown, syncChart } = useChartDrillDowns(
    groupField,
    chartRef,
    'sankey',
    chartDefinition,
  );

  useEffect(() => {
    syncChart();
  }, [syncChart, onDrillDown, rowData]);

  useEffect(() => {
    const metricId = chartDefinition.series[0]?.metricId;
    if (!metricId || !groupField) {
      setIsNoDataToDisplay(true);
      setOptions(undefined);
      return;
    }

    const { xCategories, yCategories, cells } = to2DMatrixCells({
      columnDefs,
      rowData,
      groupField,
      excludeFields: chartDefinition.series.map((s) => s.metricId),
    });

    const { links, nodes: sankeyNodes } = toSankeyLinks({
      xCategories,
      yCategories,
      cells,
    });
    if (links.length === 0) {
      setIsNoDataToDisplay(true);
      setOptions(undefined);
      return;
    }
    const nodes = sankeyNodes.map(({ id, name }) => ({ id, name }));

    const baseSankeyOptions = sankey({
      formatMetric,
      title: ' ',
      metricId,
      fontSize: 11,
      openMenu,
      groupField,
      fieldType: chartDefinition.dimensionA
        ? chartDefinition.dimensionA.fieldType
        : undefined,
      chartDef: chartDefinition,
    });

    setOptions({
      ...baseSankeyOptions,
      series: [
        {
          type: 'sankey',
          metricId,
          metricIds: [metricId],
          onDrillDown,
          animation: false,
          keys: ['from', 'to', 'weight'],
          nodes,
          data: links,
        },
      ],
    } as any as Highcharts.Options);
    setIsNoDataToDisplay(false);
  }, [
    chartDefinition,
    columnDefs,
    formatMetric,
    groupField,
    onDrillDown,
    openMenu,
    rowData,
  ]);

  return {
    options,
    isNoData: isNoDataToDisplay,
    isReady: !isInitialLoading,
  };
};

export default useSankeyData;
